import { useAddSignal } from '../../hooks/useAddSignal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  PiScrollLight, PiWarningCircleLight, PiCheckCircleLight, PiClockCountdownLight,
  PiSparkleLight, PiArrowClockwiseLight, PiCalendarLight,
  PiFilesLight, PiHourglassMediumLight, PiWarningLight, PiShieldWarningLight,
  PiUploadSimpleLight, PiTableLight, PiCalendarBlankLight, PiListChecksLight,
  PiTrashLight,
} from 'react-icons/pi';
import ContractUploadModal from './ContractUploadModal';
import dayjs from 'dayjs';
import ContractObligationsTable, { obligationHref } from './ContractObligationsTable';
import CalendarView from '../Tasks/CalendarView';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import DetailFrame from '../shared/Frames/DetailFrame';
import GlassCard from '../shared/brand/GlassCard';
import { KpiTile } from '../shared/brand/Field';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import Button from '../shared/TailwindComponents/Button';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import { getContractsDashboard, extractContract, unmarkContract, getObligationsFeed, ContractsDashboardWithPubsub, ContractRow, ObligationFeedItem } from '../../utils/contractsApi';
import { rerunOutdatedDrafts } from '../../utils/draftIntelligenceApi';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { useAppDispatch, useAppSelector } from '../../store';
import { useContractPubSub } from './useContractPubSub';
import { useLicenseGate } from '../../hooks/useLicenseGate';

type FilterKey = 'all' | 'attention' | 'expiring_soon' | 'expired';

const STATUS_BADGE: Record<string, { color: 'green' | 'yellow' | 'red' | 'gray'; label: string }> = {
  active: { color: 'green', label: 'Active' },
  expiring_soon: { color: 'yellow', label: 'Expiring soon' },
  expired: { color: 'red', label: 'Expired' },
  unknown: { color: 'gray', label: 'No dates' },
};

const RISK_BADGE: Record<string, { color: 'green' | 'yellow' | 'red'; label: string }> = {
  low: { color: 'green', label: 'Low' },
  medium: { color: 'yellow', label: 'Medium' },
  high: { color: 'red', label: 'High' },
};

/**
 * Invisible per-row subscriber: joins one extracting contract's PubSub group and
 * calls `onDone` when it finishes - replacing the old 8-second poll loop. Kept as
 * a component so the hook can run once per pending row without breaking the rules
 * of hooks.
 */
const ContractLiveRefresh: React.FC<{ uuid: string; userId: string | number | null; onDone: () => void }>
  = ({ uuid, userId, onDone }) => {
    useContractPubSub(uuid, true, onDone, userId);
    return null;
  };

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};

const CLMDashboard: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  /* Extraction is matter AI (`matter_ai`): without it a re-analyse raises the
     upgrade modal instead of N requests the server would refuse. */
  const { hasMatterAiAccess, checkAiFeatureGate } = useLicenseGate();
  const extractionLocked = !hasMatterAiAccess();
  const userId = useAppSelector((s: any) => s?.user?.userInfo?.UserID);
  const [data, setData] = useState<ContractsDashboardWithPubsub | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [updating, setUpdating] = useState(false);
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});
  // Opens straight into upload from the sidebar quick "+" (SM-1) - every
  // time it is pressed, including while already on Contracts (B3).
  const [uploadOpen, setUploadOpen] = useState(false);
  useAddSignal(() => setUploadOpen(true));
  const [view, setView] = useState<'table' | 'calendar' | 'obligations'>('table');
  const [obligations, setObligations] = useState<ObligationFeedItem[] | null>(null);
  const [obligationsLoading, setObligationsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  // Obligations view: all open, or only the overdue / due-this-week ones.
  const [dueFilter, setDueFilter] = useState<'overdue' | 'week' | null>(null);
  const [calendarMode, setCalendarMode] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [calendarDate, setCalendarDate] = useState(() => dayjs());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getContractsDashboard());
    } catch {
      setData({ contracts: [], upcoming_renewals: [], counts: { total: 0, active: 0, expiring_soon: 0, expired: 0, needs_attention: 0 } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Lazy-load the cross-contract obligations feed the first time it's viewed.
  const loadObligations = useCallback(async () => {
    setObligationsLoading(true);
    try {
      setObligations(await getObligationsFeed());
    } catch {
      setObligations([]);
    } finally {
      setObligationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if ((view === 'obligations' || view === 'calendar') && obligations === null && !obligationsLoading) loadObligations();
  }, [view, obligations, obligationsLoading, loadObligations]);

  const rows = useMemo(() => {
    let all = data?.contracts ?? [];
    if (filter === 'attention') all = all.filter((c) => c.needs_attention);
    if (filter === 'expiring_soon') all = all.filter((c) => c.status === 'expiring_soon');
    if (filter === 'expired') all = all.filter((c) => c.status === 'expired');
    const q = query.trim().toLowerCase();
    if (q) {
      all = all.filter((c) => `${c.title} ${(c.parties || []).map((p) => p.name).join(' ')}`.toLowerCase().includes(q));
    }
    return all;
  }, [data, filter, query]);

  const obligationRows = useMemo(() => {
    let all = obligations ?? [];
    if (dueFilter === 'overdue') all = all.filter((o) => o.days_to_due != null && o.days_to_due < 0);
    if (dueFilter === 'week') all = all.filter((o) => o.days_to_due != null && o.days_to_due >= 0 && o.days_to_due <= 7);
    const q = query.trim().toLowerCase();
    if (q) all = all.filter((o) => `${o.description} ${o.contract_title}`.toLowerCase().includes(q));
    return all;
  }, [obligations, dueFilter, query]);

  const openContract = (c: ContractRow) => history.push(`/contracts/${c.uuid}`);

  const toggleSelect = (uuid: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(uuid)) n.delete(uuid); else n.add(uuid);
    return n;
  });
  const toggleSelectAll = () => setSelected((s) => {
    if (rows.every((c) => s.has(c.uuid))) {
      const n = new Set(s); rows.forEach((c) => n.delete(c.uuid)); return n;
    }
    const n = new Set(s); rows.forEach((c) => n.add(c.uuid)); return n;
  });
  const clearSelection = () => setSelected(new Set());

  const bulkReanalyze = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (extractionLocked) { checkAiFeatureGate('matter_ai'); return; }
    setBulkBusy(true);
    try {
      await Promise.allSettled(ids.map((uuid) => extractContract(uuid)));
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3500, message: t('Re-analyzing {{n}} contract(s)…', { n: ids.length }) }));
      clearSelection();
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not re-analyze the selected contracts.') }));
    } finally { setBulkBusy(false); }
  }, [selected, dispatch, t, load, extractionLocked, checkAiFeatureGate]);

  const bulkUntrack = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (extractionLocked) { checkAiFeatureGate('matter_ai'); return; }
    setBulkBusy(true);
    try {
      await Promise.allSettled(ids.map((uuid) => unmarkContract(uuid)));
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3000, message: t('Removed {{n}} contract(s) from the list.', { n: ids.length }) }));
      clearSelection();
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not remove the selected contracts.') }));
    } finally { setBulkBusy(false); }
  }, [selected, dispatch, t, load]);

  const handleUpdateOutdated = useCallback(async () => {
    setUpdating(true);
    try {
      const behind = (data?.contracts ?? [])
        .filter((c) => c.document_type === 'draft' && c.intelligence_status === 'behind')
        .map((c) => c.document_uuid);
      const res = await rerunOutdatedDrafts(behind);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 4000, message: t('Updating {{n}} document(s) from their templates…', { n: res.queued }) }));
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not update the outdated documents.') }));
    } finally {
      setUpdating(false);
    }
  }, [data, dispatch, t, load]);

  const handleRetryExtraction = useCallback(async (c: ContractRow) => {
    if (extractionLocked) { checkAiFeatureGate('matter_ai'); return; }
    setRetrying((r) => ({ ...r, [c.uuid]: true }));
    try {
      await extractContract(c.uuid);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3000, message: t('Re-running extraction…') }));
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not re-run extraction.') }));
    } finally {
      setRetrying((r) => ({ ...r, [c.uuid]: false }));
    }
  }, [dispatch, t, load, extractionLocked, checkAiFeatureGate]);

  const counts = data?.counts;
  const allContracts = data?.contracts ?? [];
  const outdatedCount = allContracts.filter((c) => c.intelligence_status === 'behind').length;
  // Contracts still being analysed - each gets a live PubSub subscriber (no poll).
  const pendingContracts = useMemo(
    () => allContracts.filter((c) => c.extraction_status === 'pending' || c.extraction_status === 'processing'),
    [allContracts],
  );
  const extractingCount = pendingContracts.length;

  // Fallback poll while anything is still being analysed. PubSub is the fast
  // path (instant flip when the engine emits), but if the socket is unavailable
  // - blocked by a WAF, negotiate failing, etc. - this still refreshes the list
  // to "done" within a few seconds, so a user never has to reload by hand. It
  // only runs while something is pending and stops the moment the count hits 0.
  useEffect(() => {
    if (extractingCount === 0) return;
    const id = setInterval(() => { load(); }, 5000);
    return () => clearInterval(id);
  }, [extractingCount, load]);

  // Risk mix across analysed contracts - the headline "actual intelligence" signal.
  const riskMix = useMemo(() => {
    const m = { high: 0, medium: 0, low: 0 };
    allContracts.forEach((c) => { if (c.risk_level && m[c.risk_level] != null) m[c.risk_level] += 1; });
    return m;
  }, [allContracts]);
  const analyzedCount = riskMix.high + riskMix.medium + riskMix.low;

  const kpis: { key: FilterKey; label: string; value: number; icon: React.ReactNode; tone?: 'default' | 'warning' | 'danger' | 'info' }[] = [
    { key: 'all', label: t('Contracts'), value: counts?.total ?? 0, icon: <PiFilesLight /> },
    { key: 'expiring_soon', label: t('Expiring soon'), value: counts?.expiring_soon ?? 0, icon: <PiHourglassMediumLight />, tone: 'warning' },
    { key: 'expired', label: t('Expired'), value: counts?.expired ?? 0, icon: <PiWarningLight />, tone: 'danger' },
    { key: 'attention', label: t('Needs attention'), value: counts?.needs_attention ?? 0, icon: <PiWarningCircleLight />, tone: 'info' },
  ];

  /*
    THE LIST SECTION (R4-06): the shared filter bar over a full-width view,
    not a white card with hand-rolled pills. Table / Calendar / Obligations
    are the bar's tabs; All / Needs attention / Expiring / Expired is the
    bar's standard Filter (the KPI tiles above set the same filter); search
    covers titles and parties, or obligations and their contracts.
  */
  const STATUS_FILTER_ID = 'status';
  const DUE_FILTER_ID = 'due';
  const barFilters = view === 'table'
    ? [{ id: STATUS_FILTER_ID, name: t('Status'), options: [
        { value: 'attention', label: `${t('Needs attention')} (${counts?.needs_attention ?? 0})` },
        { value: 'expiring_soon', label: t('Expiring soon') },
        { value: 'expired', label: t('Expired') },
      ] }]
    : view === 'obligations'
      ? [{ id: DUE_FILTER_ID, name: t('Due'), options: [
          { value: 'overdue', label: t('Overdue') },
          { value: 'week', label: t('Due within 7 days') },
        ] }]
      : [];
  const barSelected: { [key: string]: string[] } = view === 'table'
    ? { [STATUS_FILTER_ID]: filter === 'all' ? [] : [filter] }
    : view === 'obligations' ? { [DUE_FILTER_ID]: dueFilter ? [dueFilter] : [] } : {};
  const onBarFilter = (id: string, values: string[]) => {
    // Single-choice: the last picked value wins, none = all.
    const last = values[values.length - 1];
    if (id === STATUS_FILTER_ID) setFilter((last as FilterKey) || 'all');
    if (id === DUE_FILTER_ID) setDueFilter((last as 'overdue' | 'week') || null);
  };

  // Contract expiries and obligation due dates, as the standard calendar's events.
  const calendarEvents = useMemo(() => [
    ...allContracts.filter((c) => c.expiry_date).map((c) => ({
      id: `expiry-${c.uuid}`,
      title: c.title,
      start_time: c.expiry_date,
      description: String(t('Contract expires')),
      event_type: 'contract',
      _href: `/contracts/${c.uuid}`,
    })),
    ...(obligations || []).filter((o) => o.due_date).map((o) => ({
      id: `obligation-${o.uuid}`,
      title: o.description,
      start_time: o.due_date,
      description: o.contract_title,
      event_type: 'reminder',
      _href: obligationHref(o),
    })),
  ], [allContracts, obligations, t]);

  return (
    <DetailFrame
      breadcrumbs={[{ name: t('Contracts'), href: '/contracts', current: true }]}
      actions={[
        { label: t('Upload contract'), onClick: () => setUploadOpen(true), icon: <PiUploadSimpleLight className="w-4 h-4" />, variant: 'primary' as const },
        ...(outdatedCount > 0 ? [{
          label: updating ? t('Updating…') : t('Update {{n}} outdated', { n: outdatedCount }),
          onClick: handleUpdateOutdated,
          icon: <PiArrowClockwiseLight className="w-4 h-4" />,
          variant: 'outline' as const,
        }] : []),
      ]}
    >
      <ContractUploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} />
      {/* Live extraction updates - one subscriber per still-analysing contract. */}
      {pendingContracts.map((c) => (
        <ContractLiveRefresh key={c.uuid} uuid={c.uuid} userId={userId} onDone={load} />
      ))}
      <div className="details-canvas h-full w-full overflow-auto custom-scrollbar-main">
        <div className="w-full px-5 py-5 space-y-5">
          <div className="flex items-center gap-3">
            {!loading && extractingCount > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-surface-sunken/60 px-2.5 py-0.5 text-[11px] text-fg-muted">
                <PiSparkleLight size={12} className="animate-pulse text-champagne" />
                {t('{{n}} being analysed…', { n: extractingCount })}
              </span>
            )}
          </div>

          {loading ? (
            <Skeleton variant="detail" />
          ) : (
            <>
              {/* KPI strip */}
              <GlassCard padding="none">
                {/* One row from md up (it wrapped to a 2x2 block), two columns on a phone. */}
                <div className="grid grid-cols-2 md:grid-cols-4 md:divide-x divide-hairline-soft">
                  {kpis.map((k) => (
                    <button key={k.key} type="button" onClick={() => { setFilter(k.key); setView('table'); }}
                      className={`text-start transition-colors duration-200 hover:bg-surface-sunken/60 focus:bg-surface-sunken/60 focus:outline-none ${filter === k.key ? 'bg-surface-sunken/50' : ''}`}>
                      <KpiTile icon={k.icon} value={k.value} label={k.label} tone={k.tone} />
                    </button>
                  ))}
                </div>
              </GlassCard>

              {/* Risk and renewals side by side, the same height, graded widths;
                  the contracts table runs full width under them. */}
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-5 lg:items-stretch">
                <div className={analyzedCount > 0 ? 'min-w-0 lg:col-span-2 [&>*]:h-full' : 'hidden'}>
                  {analyzedCount > 0 && (
                    <GlassCard icon={<PiShieldWarningLight />} title={t('Risk overview')}>
                      <div className="space-y-2.5">
                        {(['high', 'medium', 'low'] as const).map((lvl) => {
                          const n = riskMix[lvl];
                          const pct = analyzedCount ? Math.round((n / analyzedCount) * 100) : 0;
                          const bar = lvl === 'high' ? 'bg-danger-500' : lvl === 'medium' ? 'bg-warning-500' : 'bg-success-500';
                          return (
                            <div key={lvl}>
                              <div className="mb-1 flex items-center justify-between text-[11px]">
                                <span className="capitalize text-fg-body">{t(RISK_BADGE[lvl].label)} {t('risk')}</span>
                                <span className="tabular-nums text-fg-muted">{n}</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-surface-sunken">
                                <div className={`h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                        <p className="pt-1 text-[11px] text-fg-muted">
                          {t('{{a}} of {{b}} contracts analysed by PONS.', { a: analyzedCount, b: counts?.total ?? 0 })}
                        </p>
                      </div>
                    </GlassCard>
                  )}

                </div>
                <div className={`min-w-0 [&>*]:h-full ${analyzedCount > 0 ? 'lg:col-span-3' : 'lg:col-span-5'}`}>
                  <GlassCard icon={<PiClockCountdownLight />} title={t('Upcoming renewals')}>
                    {(data!.upcoming_renewals || []).length === 0 ? (
                      <p className="py-1 text-sm text-fg-muted">{t('No renewals coming up.')}</p>
                    ) : (
                      <ul className="max-h-64 space-y-2 overflow-y-auto custom-scrollbar-main">
                        {data!.upcoming_renewals.map((r) => (
                          <li key={r.contract_uuid}
                            onClick={() => history.push(`/contracts/${r.contract_uuid}`)}
                            className="flex cursor-pointer items-center justify-between gap-2 rounded-md border border-hairline-soft bg-surface-sunken/60 px-3 py-2 hover:bg-surface-sunken">
                            <div className="min-w-0">
                              <div className="truncate text-xs font-medium text-fg-body">{r.title}</div>
                              <div className="flex items-center gap-1 text-[11px] text-fg-muted">
                                <PiCalendarLight size={11} />{fmtDate(r.expiry_date)}
                              </div>
                            </div>
                            <Badge color={r.status === 'expired' ? 'red' : 'yellow'} className="shrink-0 text-[10px]">
                              {r.days_to_expiry != null && r.days_to_expiry < 0 ? t('Overdue') : t('{{n}}d', { n: r.days_to_expiry ?? 0 })}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    )}
                  </GlassCard>
                </div>
              </div>

              {/* The list: the shared bar, then the view at full width (R4-06). */}
              <div className="min-w-0 space-y-3">
                <FilterBarWithTabs
                  tabs={[
                    { label: 'Table', value: 'table', icon: <PiTableLight className="h-3.5 w-3.5" /> },
                    { label: 'Calendar', value: 'calendar', icon: <PiCalendarBlankLight className="h-3.5 w-3.5" /> },
                    { label: 'Obligations', value: 'obligations', icon: <PiListChecksLight className="h-3.5 w-3.5" /> },
                  ]}
                  activeTab={view}
                  onTabClick={(v) => { setView(v as typeof view); clearSelection(); }}
                  filters={barFilters}
                  selectedFilters={barSelected}
                  onFilterChange={onBarFilter}
                  onClearAllFilters={() => { setFilter('all'); setDueFilter(null); }}
                  onSearchChange={view === 'calendar' ? undefined : setQuery}
                  itemCount={view === 'obligations' ? obligationRows.length : view === 'table' ? rows.length : undefined}
                  totalItemCount={view === 'obligations' ? (obligations?.length ?? 0) : view === 'table' ? allContracts.length : undefined}
                  itemCountLabel={view === 'obligations' ? 'obligations' : 'contracts'}
                  selectionCount={view === 'table' ? selected.size : undefined}
                  actions={view === 'table' ? [
                    { label: t('Re-analyze'), onClick: bulkReanalyze, icon: <PiArrowClockwiseLight className="h-3.5 w-3.5" /> },
                    { label: t('Remove from list'), onClick: bulkUntrack, icon: <PiTrashLight className="h-3.5 w-3.5" /> },
                  ] : []}
                  noParentPadding
                />

                {view === 'obligations' ? (
                  <ContractObligationsTable
                    obligations={obligationRows}
                    loading={obligationsLoading || obligations === null}
                    onChange={(next) => {
                      // `next` is the filtered list after an action; fold it back into the full feed.
                      const changed = new Map(next.map((o) => [o.uuid, o]));
                      const shown = new Set(obligationRows.map((o) => o.uuid));
                      setObligations((all) => (all || [])
                        .filter((o) => !shown.has(o.uuid) || changed.has(o.uuid))
                        .map((o) => changed.get(o.uuid) || o));
                    }}
                  />
                ) : view === 'calendar' ? (
                  <div className="h-[44rem] min-h-0 overflow-hidden rounded-lg border border-hairline-soft">
                    <CalendarView
                      embedded
                      tasks={calendarEvents}
                      currentDate={calendarDate}
                      onDateChange={setCalendarDate}
                      calendarViewMode={calendarMode}
                      onCalendarViewModeChange={setCalendarMode}
                      onRangeChange={() => { /* all events are already loaded */ }}
                      onSelectEvent={(event) => { if (event?._href) history.push(event._href); }}
                      isDayLoading={obligationsLoading}
                    />
                  </div>
                ) : allContracts.length === 0 ? (
                  <EmptyState density="compact" icon={<PiScrollLight />}
                    headline={t('No contracts yet')}
                    subtext={t('Mark a draft or file as a contract to start tracking it here.')} />
                ) : (
                /* The shared Table: scrolls sideways with the name pinned,
                   parties in their own column rather than stacked under
                   the title. */
                <Table
                  columns={['Contract', 'Parties', 'Status', 'Expiry', 'Health']}
                  data={rows.map((c) => ({
                    id: c.uuid,
                    Contract: c.title,
                    Parties: (c.parties || []).map((p) => p.name).filter(Boolean).join(' · '),
                    Status: c.status,
                    Expiry: c.expiry_date || '',
                  }))}
                  tableId="contracts"
                  clientSort
                  sortableColumns={['Contract', 'Status', 'Expiry']}
                  sortAccessors={{ Expiry: (row) => (row.Expiry ? new Date(row.Expiry).getTime() : Number.MAX_SAFE_INTEGER) }}
                  defaultColumnWidths={{ Contract: 300, Parties: 220, Status: 170, Expiry: 130, Health: 260 }}
                  enableSelection
                  selectedIds={Array.from(selected)}
                  onSelect={(id) => toggleSelect(id)}
                  onSelectAll={toggleSelectAll}
                  onRowClick={(row) => { const c = rows.find((r) => r.uuid === row.id); if (c) openContract(c); }}
                  renderCell={(col, value, row) => {
                    const c = rows.find((r) => r.uuid === row.id);
                    if (!c) return value;
                    if (col === 'Contract') return <span className="text-fg-strong" title={c.title}>{c.title}</span>;
                    if (col === 'Parties') return value ? <span className="text-fg-muted" title={value}>{value}</span> : '—';
                    if (col === 'Status') {
                      const sb = STATUS_BADGE[c.status] || STATUS_BADGE.unknown;
                      return (
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          <Badge color={sb.color} className="whitespace-nowrap text-[10px]">{t(sb.label)}</Badge>
                          {c.status === 'expiring_soon' && c.days_to_expiry != null && (
                            <span className="text-[11px] text-warning-600 dark:text-warning-400">
                              {c.days_to_expiry} {c.days_to_expiry === 1 ? t('day') : t('days')}
                            </span>
                          )}
                        </span>
                      );
                    }
                    if (col === 'Expiry') return <span className="text-fg-body">{fmtDate(c.expiry_date)}</span>;
                    if (col === 'Health') {
                      return (
                        <div className="flex items-center gap-1.5 overflow-hidden">
                          {c.risk_level && RISK_BADGE[c.risk_level] && (
                            <Badge color={RISK_BADGE[c.risk_level].color} className="inline-flex items-center gap-1 text-[10px]">
                              <PiShieldWarningLight size={11} />{t(RISK_BADGE[c.risk_level].label)}
                            </Badge>
                          )}
                          {c.intelligence_status === 'behind' && (
                            <Badge color="yellow" className="inline-flex items-center gap-1 text-[10px]"><PiWarningCircleLight size={11} />{t('Template')}</Badge>
                          )}
                          {c.intelligence_status === 'aligned' && (
                            <Badge color="green" className="inline-flex items-center gap-1 text-[10px]"><PiCheckCircleLight size={11} />{t('Aligned')}</Badge>
                          )}
                          {c.risk && c.risk.open_findings > 0 && (
                            <Badge color="red" className="inline-flex items-center gap-1 text-[10px]"><PiWarningCircleLight size={11} />{t('{{n}} findings', { n: c.risk.open_findings })}</Badge>
                          )}
                          {c.extraction_status === 'pending' && (
                            <Badge color="gray" className="inline-flex items-center gap-1 text-[10px]"><PiSparkleLight size={11} />{t('Extracting…')}</Badge>
                          )}
                          {c.extraction_status === 'failed' && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); handleRetryExtraction(c); }}
                              disabled={!!retrying[c.uuid]}
                              className="inline-flex items-center gap-1 rounded-full border border-danger-300 bg-danger-50 px-2 py-0.5 text-[10px] font-medium text-danger-700 hover:bg-danger-100 disabled:opacity-60 dark:border-danger-700 dark:bg-danger-900/30 dark:text-danger-300">
                              <PiArrowClockwiseLight size={11} className={retrying[c.uuid] ? 'animate-spin' : ''} />
                              {retrying[c.uuid] ? t('Retrying…') : t('Extraction failed — retry')}
                            </button>
                          )}
                        </div>
                      );
                    }
                    return value;
                  }}
                />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </DetailFrame>
  );
};

export default CLMDashboard;
